import {
  DraftEntity,
  RawDraftContentBlock,
  RawDraftEntity,
} from '@msd-npm-gmc-dev/gmc-dc-assembler-draftjs2lexical';
import { SerializedCustomListItemNode } from 'modules/Lexical/nodes/CustomListItemNode';
import { SerializedCustomListNode } from 'modules/Lexical/nodes/CustomListNode';
import * as Models from 'models';
import { extractBlockEntities } from '../parser';
import { DraftEntityData, SerializedLexicalInlineNode } from '../types';
import { createLinkNode } from './Link';
import { createListNode } from './List';
import { createListItemNode } from './ListItem';
import { createMarkNode } from './Mark';
import { createNoWrapTextNode } from './NoWrap';
import { createReferenceCitationNode } from './ReferenceCitation';
import { createTextNode } from './Text';

export function convertEntities(
  block: RawDraftContentBlock,
  entityMap: { [key: string]: RawDraftEntity },
  fonts?: Models.BrandFontsList,
): SerializedLexicalInlineNode[] {
  const entities: DraftEntityData[] = extractBlockEntities(block, entityMap);

  return entities.map((chunk) => {
    const { type } = chunk;

    switch (type) {
      case DraftEntity.ABBREVIATION:
        return createMarkNode(chunk, [createTextNode(chunk, fonts)]);
      case DraftEntity.LINK:
        return createLinkNode(chunk, [createTextNode(chunk, fonts)]);
      case DraftEntity.NO_WRAP:
        return createNoWrapTextNode([createTextNode(chunk, fonts)]);
      case DraftEntity.REFERENCE:
        return createReferenceCitationNode(chunk, fonts);
      default:
        return createTextNode(chunk, fonts);
    }
  }, []);
}

export function createListHierarchy(
  listBlocks: [RawDraftContentBlock, SerializedLexicalInlineNode[]][],
): SerializedCustomListNode {
  const listMap: Map<number, SerializedCustomListNode> = new Map();
  const firstEntity = listBlocks[0][0];
  listMap.set(0, createListNode(firstEntity, [], 0));

  listBlocks.forEach(([block, children]) => {
    const listItem = createListItemNode(block, children);
    const depth = block.depth;

    if (listMap.has(depth)) {
      listMap.get(depth)?.children.push(listItem);
    } else {
      const closestDepth = Math.max(...Array.from(listMap.keys()).filter(d => d < depth));
      let latestList = listMap.get(closestDepth);

      // Create intermediate leaves between closest node depth and current node depth
      for (let currentDepth = closestDepth + 1; currentDepth <= depth; currentDepth++) {
        const li: SerializedCustomListItemNode = { ...listItem, indent: currentDepth, children: [] };
        latestList?.children.push(li);
        const ul = createListNode(block, [], currentDepth);
        li.children.push(ul);
        listMap.set(currentDepth, ul);
        latestList = ul;
      }

      // Add the item with content to the last list
      latestList?.children.push(listItem);
    }

    // clean previous list nodes with depth gte than current node depth
    for (const key of Array.from(listMap.keys())) {
      if (key > listItem.indent) {
        listMap.delete(key);
      }
    }
  });

  return listMap.get(0);
}
